import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(n) {
  return Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── sub-components ────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div style={styles.card}>
      <div style={{ ...styles.header, background: '#1a1a2e' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,.15)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: 10, width: 60, background: 'rgba(255,255,255,.2)', borderRadius: 4, marginBottom: 6 }} />
          <div style={{ height: 14, width: 140, background: 'rgba(255,255,255,.2)', borderRadius: 4 }} />
        </div>
      </div>
      <div style={styles.body}>
        {[1, 2].map(i => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ height: 12, width: '55%', background: '#e5e7eb', borderRadius: 4 }} />
            <div style={{ height: 12, width: '15%', background: '#e5e7eb', borderRadius: 4 }} />
          </div>
        ))}
        <div style={{ height: 1, background: '#e5e7eb', margin: '16px 0' }} />
        <div style={{ height: 20, width: '40%', background: '#e5e7eb', borderRadius: 4, marginLeft: 'auto' }} />
        <div style={{ height: 48, background: '#e5e7eb', borderRadius: 10, marginTop: 24 }} />
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div style={styles.card}>
      <div style={styles.body}>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <p style={{ fontWeight: 600, fontSize: 16, color: '#111827', marginBottom: 6 }}>Quote not found</p>
          <p style={{ fontSize: 13, color: '#6b7280' }}>This link may have expired or the quote was removed.</p>
        </div>
      </div>
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [notFound, setNotFound]     = useState(false);
  const [approved, setApproved]     = useState(false);
  const [approving, setApproving]   = useState(false);
  const [approveError, setApproveError] = useState('');

  // Payment flow state
  const [paymentState, setPaymentState]   = useState(null); // null | 'paying' | 'success' | 'cancelled'
  const [paymentError, setPaymentError]   = useState('');

  // Detect payment return params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentState('success');
    } else if (params.get('payment') === 'cancelled') {
      setPaymentState('cancelled');
    }
  }, []);

  useEffect(() => {
    if (!token) { setNotFound(true); setLoading(false); return; }
    fetch(`${API}/api/public/quote/${token}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        if (!r.ok) throw new Error('Failed to load quote');
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setQuote(data);
        if (data.status === 'approved') setApproved(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleApprove = async () => {
    setApproving(true);
    setApproveError('');
    try {
      const res = await fetch(`${API}/api/public/quotes/${token}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setApproved(true);
        return;
      }
      if (res.status === 429) {
        setApproveError('Too many attempts. Please wait a few minutes and try again.');
      } else if (res.status === 404) {
        setApproveError('This quote link is no longer valid. Please request a fresh link.');
      } else {
        setApproveError(data.detail || data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setApproveError('Network error. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handlePay = async (paymentType) => {
    setPaymentState('paying');
    setPaymentError('');
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/api/public/quotes/${token}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_type: paymentType, origin_url: originUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (res.status === 429) {
        setPaymentError('Too many payment attempts. Please wait a few minutes and try again.');
      } else if (res.status === 404) {
        setPaymentError('This quote link is no longer valid. Please request a fresh link.');
      } else {
        setPaymentError(data.detail || 'Payment setup failed. Please try again.');
      }
      setPaymentState(null);
    } catch {
      setPaymentError('Network error. Please try again.');
      setPaymentState(null);
    }
  };

  const brandColor = quote?.brandColor || '#0A2647';
  const isRedirecting = paymentState === 'paying';

  return (
    <div style={styles.page}>
      {loading  && <Skeleton />}
      {notFound && !loading && <NotFound />}

      {quote && !loading && (
        <div style={styles.card}>
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div style={{ ...styles.header, background: brandColor }}>
            <div style={styles.avatar(quote.logoUrl, quote.companyName)}>
              {quote.logoUrl
                ? <img src={quote.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, borderRadius: '50%' }} />
                : (quote.companyName || 'CO').slice(0, 2).toUpperCase()
              }
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', opacity: .85, color: '#fff' }}>Quote</div>
              <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {quote.companyName || 'Your Service Provider'}
              </div>
            </div>
          </div>

          {/* ── Body ───────────────────────────────────────────────────── */}
          <div style={styles.body}>
            <p style={{ margin: '0 0 4px 0', fontSize: 13, color: '#6b7280' }}>Prepared for</p>
            <p style={{ margin: '0 0 20px 0', fontSize: 16, fontWeight: 600, color: '#111827' }}>
              {quote.clientName}
            </p>

            {/* ── Frequency badge ──────────────────────────────────────── */}
            {quote.frequency && (
              <div style={styles.badge(brandColor)}>
                {quote.frequency} Service Plan
              </div>
            )}

            {/* ── Line items ───────────────────────────────────────────── */}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th('left')}>Service</th>
                  <th style={styles.th('right')}>Price</th>
                </tr>
              </thead>
              <tbody>
                {(quote.lineItems || []).map((li, i) => (
                  <tr key={i}>
                    <td style={styles.td('left')}>{li.description || 'Service'}</td>
                    <td style={styles.td('right')}>${fmt(li.price)}</td>
                  </tr>
                ))}
                {(!quote.lineItems || quote.lineItems.length === 0) && (
                  <tr>
                    <td style={styles.td('left')}>Service</td>
                    <td style={styles.td('right')}>${fmt(quote.total)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ── Tax breakdown (only if tax was applied) ───────────────── */}
            {(quote.taxRate > 0 || quote.taxAmount > 0) && (
              <div style={{ marginTop: 8 }}>
                <div style={styles.summaryRow}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Subtotal</span>
                  <span style={{ fontSize: 13, color: '#374151' }}>${fmt(quote.subtotal)}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Tax ({quote.taxRate}%)</span>
                  <span style={{ fontSize: 13, color: '#374151' }}>${fmt(quote.taxAmount)}</span>
                </div>
              </div>
            )}

            {/* ── Grand total ──────────────────────────────────────────── */}
            <div style={styles.totalRow}>
              <span style={{ color: '#6b7280', marginRight: 10, fontSize: 13 }}>Total</span>
              <span style={{ fontWeight: 700, fontSize: 20, color: brandColor }}>${fmt(quote.total)}</span>
            </div>

            {/* ── Notes ────────────────────────────────────────────────── */}
            {quote.notes && (
              <div style={styles.notes}>{quote.notes}</div>
            )}

            {/* ──────────────────────────────────────────────────────────── */}
            {/* CTA area — four mutually-exclusive states:                  */}
            {/*  1. Payment success (returned from Stripe)                  */}
            {/*  2. Already paid (existing paid invoice on load)            */}
            {/*  3. Approved + payment options                              */}
            {/*  4. Not yet approved — approval button                      */}
            {/* ──────────────────────────────────────────────────────────── */}

            {/* State 1: Payment success — returned from Stripe */}
            {paymentState === 'success' && (
              <div style={styles.paymentSuccessBox} data-testid="payment-success-state">
                <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#14532d', marginBottom: 6 }}>
                  Payment Received
                </div>
                <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
                  Your booking is confirmed.<br />
                  <strong>{quote.companyName}</strong> will be in touch to schedule your service.
                </div>
              </div>
            )}

            {/* State 2: Already paid (loaded from DB check) */}
            {paymentState !== 'success' && quote.alreadyPaid && (
              <div style={styles.alreadyPaidBox} data-testid="already-paid-state">
                <div style={{ fontSize: 22, marginBottom: 8 }}>💳</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#1e40af', marginBottom: 4 }}>
                  Payment Already Received
                </div>
                <div style={{ fontSize: 13, color: '#4b5563' }}>
                  A payment has been recorded for this quote. {quote.companyName} has been notified.
                </div>
              </div>
            )}

            {/* State 3: Approved — show payment options */}
            {paymentState !== 'success' && !quote.alreadyPaid && approved && (
              <div data-testid="payment-options-section">
                {/* Approval confirmation banner */}
                <div style={styles.approvalBanner}>
                  <span style={{ fontSize: 16, marginRight: 8 }}>✓</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d' }}>Quote Accepted</div>
                    <div style={{ fontSize: 12, color: '#4b5563', marginTop: 2 }}>
                      Secure your booking with a payment below.
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  {/* Deposit button — only if enabled */}
                  {quote.depositEnabled && (
                    <button
                      onClick={() => handlePay('deposit')}
                      disabled={isRedirecting}
                      data-testid="pay-deposit-btn"
                      style={styles.primaryPayBtn(brandColor, isRedirecting)}
                    >
                      {isRedirecting
                        ? 'Redirecting to secure payment…'
                        : `Secure Your Spot – Pay Deposit ($${fmt(quote.depositAmount)})`}
                    </button>
                  )}

                  {/* Full payment button */}
                  <button
                    onClick={() => handlePay('full')}
                    disabled={isRedirecting}
                    data-testid="pay-full-btn"
                    style={quote.depositEnabled
                      ? styles.secondaryPayBtn(isRedirecting)
                      : styles.primaryPayBtn(brandColor, isRedirecting)
                    }
                  >
                    {isRedirecting
                      ? 'Redirecting to secure payment…'
                      : `Pay in Full ($${fmt(quote.total)})`}
                  </button>

                  {/* Cancelled notice */}
                  {paymentState === 'cancelled' && (
                    <div style={styles.cancelledBox} data-testid="payment-cancelled-notice">
                      Payment was not completed. You can try again above.
                    </div>
                  )}

                  {paymentError && (
                    <div style={styles.errorBox} data-testid="payment-error">
                      {paymentError}
                    </div>
                  )}

                  <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                    Payments are processed securely via Stripe.
                  </p>
                </div>
              </div>
            )}

            {/* State 4: Not yet approved */}
            {paymentState !== 'success' && !quote.alreadyPaid && !approved && (
              <div style={{ marginTop: 24 }}>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  data-testid="approve-quote-btn"
                  style={styles.approveBtn(brandColor, approving)}
                >
                  {approving ? 'Processing...' : 'Approve This Quote'}
                </button>
                <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 8, margin: '8px 0 0 0' }}>
                  By approving, you agree to the quoted price above.
                </p>
                {approveError && (
                  <div style={styles.errorBox} data-testid="approve-quote-error">
                    {approveError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: '#f3f4f6',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '24px 16px 48px',
    fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 520,
    background: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 18px 45px rgba(15,23,42,.14)',
    border: '1px solid #e5e7eb',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 18px',
    borderBottom: '1px solid rgba(255,255,255,.12)',
  },
  avatar: (logoUrl, name) => ({
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'rgba(15,23,42,.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 16,
    color: '#fff',
    overflow: 'hidden',
    flexShrink: 0,
  }),
  body: {
    padding: '20px 18px 28px',
  },
  badge: (color) => ({
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    padding: '3px 10px',
    borderRadius: 99,
    background: `${color}18`,
    color: color,
    border: `1px solid ${color}40`,
    marginBottom: 16,
  }),
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 4,
  },
  th: (align) => ({
    textAlign: align,
    padding: '5px 8px',
    fontSize: 11,
    color: '#9ca3af',
    fontWeight: 500,
    borderBottom: '1px solid #e5e7eb',
    textTransform: 'uppercase',
    letterSpacing: '.06em',
  }),
  td: (align) => ({
    textAlign: align,
    padding: '10px 8px',
    fontSize: 14,
    color: '#111827',
    borderBottom: '1px solid #f3f4f6',
  }),
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #e5e7eb',
  },
  notes: {
    marginTop: 16,
    padding: '12px 14px',
    background: '#f9fafb',
    borderRadius: 8,
    fontSize: 13,
    color: '#4b5563',
    borderLeft: '3px solid #e5e7eb',
  },
  approvalBanner: {
    marginTop: 20,
    padding: '12px 16px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    color: '#166534',
  },
  primaryPayBtn: (color, disabled) => ({
    display: 'block',
    width: '100%',
    padding: '15px 20px',
    background: disabled ? '#9ca3af' : color,
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '.01em',
    marginTop: 10,
    transition: 'opacity .15s',
  }),
  secondaryPayBtn: (disabled) => ({
    display: 'block',
    width: '100%',
    padding: '13px 20px',
    background: disabled ? '#f3f4f6' : '#fff',
    color: disabled ? '#9ca3af' : '#374151',
    border: '1.5px solid #d1d5db',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    marginTop: 10,
    transition: 'border-color .15s',
  }),
  approveBtn: (color, disabled) => ({
    width: '100%',
    padding: '15px 20px',
    background: disabled ? '#9ca3af' : color,
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '.02em',
    transition: 'opacity .15s, transform .1s',
    display: 'block',
  }),
  paymentSuccessBox: {
    marginTop: 24,
    padding: '24px 18px',
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 12,
    textAlign: 'center',
  },
  alreadyPaidBox: {
    marginTop: 24,
    padding: '20px 18px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 12,
    textAlign: 'center',
  },
  cancelledBox: {
    marginTop: 12,
    padding: '10px 14px',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    fontSize: 13,
    color: '#92400e',
    textAlign: 'center',
  },
  errorBox: {
    marginTop: 10,
    padding: '10px 14px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    fontSize: 13,
    color: '#dc2626',
    textAlign: 'center',
  },
};
